import React, { useState } from 'react'
import { com } from '@eidu/web'
import { Text } from '@common/ui'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import useDebounced from '../../ui/debounce/useDebounced'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../../util/EqualityHashMap'
import useReferenceFieldError from '../../domain/entity/useReferenceFieldError'
import EntityFormReferenceInput from './EntityFormReferenceInput'
import EntityFormTextInput from './EntityFormTextInput'
import EntityFormDateInput from './EntityFormDateInput'
import EntityFormNumberInput from './EntityFormNumberInput'
import ReferenceSetList from './ReferenceSetList'
import Field = com.eidu.entity.field.Field
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import EntityId = com.eidu.entity.EntityId
import FieldType = com.eidu.entity.field.FieldType

export type EntityFormInputProps = {
  field: Field
  fieldValue: DraftFieldValue | undefined
  entityTypes: ReadonlyMap<EntityTypeId, EntityType | null>
  updateField: (value: DraftFieldValue) => void
  loading?: boolean
  disabled?: boolean
  dropdownWithinPortal?: boolean
}

const EntityFormInput = ({
  field,
  fieldValue,
  entityTypes,
  updateField,
  loading = false,
  disabled = false,
  dropdownWithinPortal = true,
}: EntityFormInputProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounced(searchQuery)

  const referenceableEntityTypeIds: EntityTypeId[] = Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? [])

  const referenceableEntityTypesById: Map<EntityTypeId, EntityType | null> = new EqualityHashMap(
    [...(entityTypes || [])].filter(([id]) => referenceableEntityTypeIds.some((it) => it.equals(id)))
  )

  // We only support one reference type per field at the moment. In the future, when we allow multiple, we need to change this behavior
  const firstReferenceTypeId = referenceableEntityTypeIds[0]
  const shouldLoadEntities = referenceableEntityTypeIds.length === 1 && firstReferenceTypeId !== undefined

  const {
    entities: referenceableEntities,
    loadMore,
    hasMore,
    loading: loadingEntities,
  } = usePaginatedEntitiesOfType({
    typeId: firstReferenceTypeId ?? referenceableEntityTypeIds[0] ?? entityTypes.keys().next().value,
    searchQuery: debouncedSearchQuery,
    allTypes: entityTypes,
  })

  const referenceableEntitiesByTypeId: ReadonlyMap<
    EntityTypeId,
    ReadonlyMap<EntityId, EntityWithLabelAndRelated>
  > = shouldLoadEntities && firstReferenceTypeId
    ? new EqualityHashMap([
        [firstReferenceTypeId, new EqualityHashMap(referenceableEntities.map((e) => [e.entity.id, e]))],
      ])
    : new Map()

  const { hasError: error, selectedEntity } = useReferenceFieldError(field, fieldValue, loading)

  switch (field.type) {
    case FieldType.Text:
      return (
        <EntityFormTextInput
          field={field}
          fieldValue={fieldValue}
          updateField={updateField}
          error={error}
          loading={loading}
          disabled={disabled}
        />
      )
    case FieldType.Date:
      return (
        <EntityFormDateInput
          field={field}
          fieldValue={fieldValue}
          updateField={updateField}
          error={error}
          loading={loading}
          disabled={disabled}
          dropdownWithinPortal={dropdownWithinPortal}
        />
      )
    case FieldType.Number:
      return (
        <EntityFormNumberInput
          field={field}
          fieldValue={fieldValue}
          updateField={updateField}
          error={error}
          loading={loading}
          disabled={disabled}
        />
      )
    case FieldType.Reference:
      return (
        <EntityFormReferenceInput
          field={field}
          fieldValue={fieldValue}
          referenceableEntityTypesById={referenceableEntityTypesById}
          referenceableEntitiesByTypeId={referenceableEntitiesByTypeId}
          selectedEntity={selectedEntity}
          updateField={updateField}
          error={error}
          setSearchQuery={setSearchQuery}
          loadMore={loadMore}
          hasMore={hasMore}
          loading={loadingEntities}
          disabled={disabled || loading}
          dropdownWithinPortal={dropdownWithinPortal}
        />
      )
    case FieldType.ReferenceSet:
      return <ReferenceSetList label={field.name} />
    default:
      return (
        <Text key={`field-${field.id.toString()}`}>
          Field &quot;{field.name}&quot; has unsupported type {field.type.name}
        </Text>
      )
  }
}

export default EntityFormInput
